module Client.Page.User.MapSensorDataPage

open Client.Components.Graph.DistanceSensor
open Client.Components.Graph.FourTimesSoilSensor
open Client.Components.Graph.PressureSensor
open Client.Components.Graph.SoilPhSensor
open Client.Components.Graph.WaterQualitySensor
open Client.Components.Graph.WeatherStation
open Client.Components.GraphCommon
open Client.Domain
open Client.DomainTypes
open Client.Infrastructure
open Client.InfrastructureTypes
open Client.Page.User.MySensorsData
open Fable.FontAwesome
open Fable.React.Props
open Fable.Remoting.Client
open Client.Components
open Fable.React
open Feliz.Bulma
open Microsoft.FSharp.Collections
open Shared
open System
open Client.Infrastructure.Api
open Elmish
open Client.DomainTypes.Msg
open Client.Components.Graph.AirSensor
open Client.Components.Graph.SoilSensor
open Client.Components.Graph.RainFallSensor
open Client.Components.Graph.LeafletMoistureSensor
open Client.Components.Graph.AverageWindSpeedSensor
open Client.Components.Graph.LiquidLevelSensor
open Client.Components.Graph.SolarRadiationSensor
open Client.Domain.PageSkeleton
open Fulma
open Fulma.Elmish
open Shared.Dto
open Shared.Dto.Dto
open Shared.Dto.MapSensorData

open Feliz
open Shared.Dto.SensorGraphData
open Shared.DtoTypes.DateRangeDto
open Shared.Infrastructure

module R = Standard
module P = Fable.React.Props


let private errorConfirmationConfig: ErrorConfirmation.Config<Msg> = {
    Handler = ErrorConfirmation.createHandler (MySensSensorMsg.Reload >> MapSensorData)
    TimeoutMilliseconds = 5000
}

type SensorGraphDataList =
    | AirList of AirSensorGraphData
    | GroundList of SoilSensorGraphData
    | RainFallList of RainFallSensorGraphData
    | LeafletMoistureList of SimpleGraphData<LeafletMoistureGraphNode>
    | SoilPhList of SimpleGraphData<SoilPhGraphNode>
    | AverageWindSpeedList of AverageWindSpeedGraphsData
    | WeatherStationList of WeatherStationGraphData
    | LiquidLevelList of SimpleGraphData<LiquidLevelGraphNode>
    | SolarRadiationList of SimpleGraphData<SolarRadiationGraphNode>
    | FourTimesSoilList of FourTimesSoilSensorGraphData
    | PressureList of SimpleGraphData<PressureGraphNode>
    | DistanceList of SimpleGraphData<DistanceGraphNode>
    | WaterQualityList of SimpleGraphData<WaterQualityGraphNode>

type DatePickerModel = {
    State: DatePicker.Types.State
    CurrentDate: DateTime option
}

type HistoryGraphData =
    | NotLoaded
    | NoData
    | Data of SensorGraphDataList

type DataModel = {
    Session: UserSession
    Name: string
    Location: Location
    SensorId: int
    CurrentData: MapSensorData
    HasForecast: bool
    GraphRequestRunning: bool
    DownloadRequestRunning: bool
    DownloadRange: DateRangeOption
    AvailableDownloadRanges: DateRangeOption list
    GraphDateRange: DateRangePicker.Data
    HistoryGraphData: HistoryGraphData
}

type PageModel =
    | NotFound
    | NotActive
    | NotAllowed
    | NotAuthenticated
    | Data of DataModel

type Model = Loadable<PageModel, UserSession>

let init id (session: UserSession) : Model * Cmd<Msg> =
    let requestData = {
        SessionKey = session.SessionKey
        Data = id
    }

    let cmd =
        Cmd.OfAsync.either
            api.getSensorDetailPageData
            requestData
            (MySensSensorMsg.PageDataReceived >> MapSensorData)
            (MySensSensorMsg.LoadingFailed >> MapSensorData)

    Loadable.Loading session, cmd

let pageDataToDataModel (session: UserSession) (data: SensorDetailPage.Data) : DataModel =
    let init =
        match data.HasForecast with
        | true -> DateRangePicker.initWithTwoDaysAndForecast
        | false -> DateRangePicker.initWithTwoDays

    let graphDateRange = init data.VisualisationTimeSpan

    let dateRanges = data.DownloadRangeIds |> List.choose getRangeFromId

    {
        Session = session
        Name = data.Name
        SensorId = data.Id
        CurrentData = data.CurrentData
        Location = data.Location
        HasForecast = data.HasForecast
        GraphRequestRunning = false
        DownloadRequestRunning = false
        DownloadRange = List.head dateRanges
        AvailableDownloadRanges = dateRanges
        GraphDateRange = graphDateRange
        HistoryGraphData = NotLoaded
    }

let onDetailPageDataReceived session (response: SensorDetailPage.Response) : PageModel =
    match response with
    | SensorDetailPage.Response.NotFound -> PageModel.NotFound
    | SensorDetailPage.Response.Unauthenticated -> PageModel.NotAuthenticated
    | SensorDetailPage.Response.Unauthorized -> PageModel.NotAllowed
    | SensorDetailPage.Response.NotActive -> PageModel.NotActive
    | SensorDetailPage.Response.Data data -> PageModel.Data(pageDataToDataModel session data)


let createHistoricRequestData (sensor: int) (startDate: DateTime) (endDate: DateTime) = {
    Range = {
        Start = startDate
        End = endDate
    }

    SensorId = sensor
}

let createRequestHistoricDataCmd data =
    Cmd.OfAsync.either
        api.getHistoricMapSensorData
        data
        (MySensSensorMsg.HistoricDataReceived >> MapSensorData)
        (LoadingHistoricDataFailed >> MapSensorData)

let toDatePickerModel (newState: DatePicker.Types.State) (date: DateTime option) = {
    State = newState
    CurrentDate = date
}


let toGraphDataList (data: GraphDataDto) =
    if isGraphDataEmpty data then
        NoData
    else
        let graphData =
            match data with
            | Air airData -> airData |> graphDataDtoToAirGraphData |> AirList
            | Soil soilData -> soilData |> graphDataDtoToSoilGraphData |> GroundList
            | RainFall rainFallData -> rainFallData |> graphDataDtoToRainFallGraphData |> RainFallList
            | LeafletMoisture leafletMoistureData ->
                leafletMoistureData |> graphDataToLeafletMoistureData |> LeafletMoistureList
            | SoilPh soilPhData -> soilPhData |> graphDataToSoilPhGraphData |> SoilPhList
            | AverageWindSpeed averageWindSpeedData ->
                averageWindSpeedData
                |> graphDataToAverageWindSpeedGraphData
                |> AverageWindSpeedList
            | WeatherStation data -> data |> graphDataDtoToWeatherStationGraphData |> WeatherStationList
            | LiquidLevel liquidLevelData -> liquidLevelData |> graphDataToLiquidLevelGraphData |> LiquidLevelList
            | SolarRadiation data -> data |> graphDataToSolarRadiationGraphData |> SolarRadiationList
            | FourTimesSoil data -> data |> graphDataDtoToFourTimesSoilGraphData |> FourTimesSoilList
            | Pressure data -> data |> graphDataToPressureGraphData |> PressureList
            | Distance data -> data |> graphDataToDistanceGraphData |> DistanceList
            | WaterQuality data -> data |> graphDataToWaterQualityGraphData |> WaterQualityList

        HistoryGraphData.Data graphData

let private createExcelFileName (requestData: SensorDataRequest) (data: DataModel) =
    let startFileName = DateTime.toDateFileNameString requestData.Range.Start
    let endFileName = DateTime.toDateFileNameString requestData.Range.End

    let rangeFileNamePart =
        if startFileName = endFileName then
            startFileName
        else
            sprintf "%s-%s" startFileName endFileName

    sprintf "%s_%s.xlsx" data.Name rangeFileNamePart

let private startDownload (data: DataModel) (requestData: SensorDataRequest) =
    let authenticatedRequest = {
        SessionKey = data.Session.SessionKey
        Data = requestData
    }

    let data = Loadable.Data(Data { data with DownloadRequestRunning = true })

    let cmd =
        Cmd.OfAsync.either
            api.downloadMapSensorData
            authenticatedRequest
            (fun response -> MySensSensorMsg.DownloadFinished(requestData, response) |> MapSensorData)
            (MySensSensorMsg.DownloadFailed >> MapSensorData)

    data, cmd

let update (msg: MySensSensorMsg) (model: Model) : Model * Cmd<Msg> =
    match msg, model with
    | PageDataReceived response, Loadable.Loading loadingModel ->
        let dataModel = onDetailPageDataReceived loadingModel response

        match dataModel with
        | Data data ->
            let now = DateTime.Now

            let requestData =
                createHistoricRequestData
                    data.SensorId
                    (now |> DateTime.addDays -2 |> DateTime.startOfDay)
                    (now |> DateTime.addDays 1 |> DateTime.endOfDay)

            Loadable.Data(Data { data with GraphRequestRunning = true }),
            Cmd.OfAsync.either
                api.getHistoricMapSensorData
                requestData
                (MySensSensorMsg.HistoricDataReceived >> MapSensorData)
                (MySensSensorMsg.LoadingHistoricDataFailed >> MapSensorData)
        | _ -> Loadable.Data dataModel, Cmd.none
    | HistoricDataReceived historicData, Loadable.Data(Data data) ->
        let newGraphData = toGraphDataList historicData

        let newData = {
            data with
                HistoryGraphData = newGraphData
                GraphRequestRunning = false
        }

        Loadable.Data(Data newData), Cmd.none
    | DownloadDateRangeChanged selectedRange, Loadable.Data(Data data) ->
        Loadable.Data(Data { data with DownloadRange = selectedRange }), Cmd.none
    | MySensSensorMsg.GraphDateRangeChanged dates, Loadable.Data(Data data) ->
        let newGraphDateRange = DateRangePicker.updateData dates data.GraphDateRange

        Loadable.Data(Data { data with GraphDateRange = newGraphDateRange }), Cmd.none
    | MySensSensorMsg.RequestData requestData, Loadable.Data(Data data) ->
        Loadable.Data(Data { data with GraphRequestRunning = true }), createRequestHistoricDataCmd requestData
    | StartDownload requestData, Loadable.Data(Data data) -> startDownload data requestData
    | MySensSensorMsg.DownloadFinished(requestData, response: AuthenticatedResponse<byte[]>), Loadable.Data(Data data) ->
        let cmd =
            match response with
            | Result.Ok responseData ->
                let fileName = createExcelFileName requestData data

                responseData.SaveFileAs(fileName, "application/vnd.ms-excel")

                Cmd.none
            | Result.Error err -> ErrorConfirmation.responseError errorConfirmationConfig err

        Loadable.Data(Data { data with DownloadRequestRunning = false }), cmd
    | MySensSensorMsg.LoadingFailed _, _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | MySensSensorMsg.DownloadFailed ex, Loadable.Data(Data data) ->
        let cmd =
            ErrorConfirmation.dataTransmissionErrorConfirmation errorConfirmationConfig ex

        Loadable.Data(Data { data with DownloadRequestRunning = false }), cmd
    | MySensSensorMsg.LoadingHistoricDataFailed ex, Loadable.Data(Data data) ->
        let cmd =
            ErrorConfirmation.dataTransmissionErrorConfirmation errorConfirmationConfig ex

        Loadable.Data(Data { data with GraphRequestRunning = false }), cmd
    | MySensSensorMsg.Reload reload, _ ->
        ErrorConfirmation.doReload reload

        model, Cmd.none
    | _ -> model, Cmd.none

let currentDataBox (data: MapSensorData) = [
    (Heading.h3 [
        Heading.Modifiers [
            Modifier.TextAlignment(Screen.All, TextAlignment.Option.Centered)
        ]
    ] [ str "Aktuelle Daten" ])

    yield!
        match data with
        | NoDataAvailable
        | NoPublicData -> []
        | AirData air -> currentAirDataBox air
        | SoilData ground -> currentSoilDataBox ground
        | FourTimesSoilData data -> currentFourTimesSoilDataBox data
        | RainFallData rainFall -> currentRainfallDataBox rainFall
        | LeafletMoistureData leafletMoistureSensorData -> currentLeafletMoistureDataBox leafletMoistureSensorData
        | SoilPhData data -> currentSoilPhDataBox data
        | AverageWindSpeedData data -> currentAverageWindSpeedDataBox data
        | LiquidLevelData data -> currentLiquidLevelDataBox data
        | SolarRadiationData data -> currentSolarRadiationDataBox data
        | WeatherStationData data -> [] // TODO
        | DistanceData data -> currentDistanceDataBox data
        | PressureData data -> currentPressureDataBox data
        | WaterQualityData data -> currentWaterQualityDataBox data
]

let downloadBox dispatch (sensorId: int) (model: DataModel) =
    let currentTime = DateTime.Now

    let requestData =
        createHistoricRequestData
            sensorId
            (model.DownloadRange.StartTime currentTime)
            (model.DownloadRange.EndTime currentTime)

    let buttonOptions = [
        color.isLink
        if model.DownloadRequestRunning then
            button.isLoading
        else
            ()

        prop.onClick (fun _ -> dispatch (MySensSensorMsg.StartDownload requestData |> MapSensorData))
    ]

    let content =
        Bulma.columns [
            columns.isVCentered
            prop.children [
                Bulma.column [
                    column.isNarrow
                    prop.children [ Html.p [ prop.text "Datumsbereich:" ] ]
                ]
                Bulma.column [
                    DateRangeSelect.view
                        model.DownloadRange
                        ((MySensSensorMsg.DownloadDateRangeChanged >> MapSensorData) >> dispatch)
                        model.AvailableDownloadRanges
                ]
                Bulma.column [
                    column.isNarrow
                    prop.children [
                        Bulma.button.button [ yield! buttonOptions; prop.text "Download" ]
                    ]
                ]
            ]
        ]

    Bulma.box [
        (Heading.h3 [
            Heading.Modifiers [
                Modifier.TextAlignment(Screen.All, TextAlignment.Option.Centered)
            ]
        ] [ str "Download der Daten" ])
        content
    ]


let getGraphs (hasForecast: bool) (data: SensorGraphDataList) =
    match data with
    | AirList air -> airDataGraphs hasForecast air
    | GroundList ground -> soilDataGraphs ground
    | RainFallList rainFall -> rainFallDataGraphs hasForecast rainFall
    | LeafletMoistureList moisture -> leafletMoistureDataGraphs moisture
    | SoilPhList soilPh -> soilPhDataGraphs soilPh
    | AverageWindSpeedList averageWind -> averageWindSpeedDataGraphs averageWind
    | WeatherStationList weatherStation -> weatherStationDataGraphs weatherStation
    | LiquidLevelList liquidLevel -> liquidLevelGraphs liquidLevel
    | SolarRadiationList solarRadiation -> solarRadiationGraphs solarRadiation
    | FourTimesSoilList data -> fourTimesSoilDataGraphs data
    | PressureList pressure -> pressureDataGraphs pressure
    | DistanceList distance -> distanceDataGraphs distance
    | WaterQualityList waterQuality -> waterQualityGraphs waterQuality

let createEnabledButtonOptions dispatch (sensorId: int) (startDate: DateTime) (endDate: DateTime) =
    let requestData =
        createHistoricRequestData sensorId (DateTime.startOfDay startDate) (DateTime.endOfDay endDate)

    [
        Button.Disabled false
        Button.OnClick(fun _ -> dispatch (MySensSensorMsg.RequestData requestData |> MapSensorData))
    ]

let createDatePicker (rangePicker: DateRangePicker.Data) (model: DataModel) dispatch =
    let buttonOptions =
        Option.map2 (createEnabledButtonOptions dispatch model.SensorId) rangePicker.Start rangePicker.End
        |> Option.defaultValue [ Button.Disabled true ]

    let buttonOptions =
        List.append buttonOptions [
            Button.IsLink
            Button.IsLoading model.GraphRequestRunning
        ]

    Column.column [ Column.Width(Screen.All, Column.IsFull) ] [
        Columns.columns [ Columns.IsVCentered ] [
            Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ p [] [ str "Datumsbereich:" ] ]
            Column.column [] [
                DateRangePicker.view
                    "Von/Bis Datumsbereich auswählen"
                    ((MySensSensorMsg.GraphDateRangeChanged >> MapSensorData) >> dispatch)
                    rangePicker
            ]
            Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ Button.button buttonOptions [ str "Laden" ] ]
        ]
    ]

let graphsView (hasForecast: bool) (data: HistoryGraphData) =
    match data with
    | NotLoaded -> []
    | NoData -> [
        createFullColumn (boxStringSubtitle "Es wurden keine Daten im ausgewählten Zeitraum gefunden")
      ]
    | HistoryGraphData.Data data -> getGraphs hasForecast data

let graphBoxContent dispatch (model: DataModel) =
    let heading =
        Heading.h3 [
            Heading.Modifiers [
                Modifier.TextAlignment(Screen.All, TextAlignment.Option.Centered)
            ]
        ] [ str "Visualisierung der Daten" ]

    let graphsContent =
        let datePicker = createDatePicker model.GraphDateRange model dispatch

        Columns.columns [ Columns.IsMultiline ] [
            datePicker
            yield! graphsView model.HasForecast model.HistoryGraphData
        ]

    Box.box' [ GenericOption.CustomClass "grey_box" ] [ heading; graphsContent ]

let private createMapLink dispatch (location: Location) : ReactElement =
    a [
        location
        |> Some
        |> Route.SensorMap
        |> Clickable.onClickGoToRoute dispatch
        |> OnClick
    ] [
        Icon.icon [] [
            Fa.i [ Fa.Size Fa.FaLarge; Fa.Solid.MapMarkedAlt ] []
        ]
    ]

let dataView dispatch (model: DataModel) =
    let boxes = [
        Box.box' [] (currentDataBox model.CurrentData)
        graphBoxContent dispatch model
        downloadBox dispatch model.SensorId model
    ]

    let heading =
        Container.container [] [
            Columns.columns [
                Columns.IsCentered
                Columns.IsVCentered
                Columns.IsMultiline
            ] [
                Column.column [
                    Column.Width(Screen.All, Column.IsNarrow)
                    Column.CustomClass "has-text-centered"
                ] [ Heading.h3 [] [ str model.Name ] ]
                Column.column [
                    Column.Width(Screen.All, Column.IsNarrow)
                    Column.CustomClass "has-text-centered"
                ] [ createMapLink dispatch model.Location ]
            ]
        ]

    let content = [
        heading
        Columns.columns [ Columns.IsMultiline ] (List.map createFullColumn boxes)
    ]

    Container.container [ Container.isFullWidth ] content

let pageView dispatch (model: PageModel) =
    match model with
    | NotFound -> headingOnlyPageContent "Der Sensor wurde nicht gefunden"
    | NotActive -> headingOnlyPageContent "Dieser Sensor ist nicht aktiv"
    | NotAllowed -> headingOnlyPageContent "Sie dürfen diese Seite nicht ansehen"
    | NotAuthenticated -> headingOnlyPageContent "Sie sind aktuell nicht eingeloggt, bitte laden Sie diese Seite neu"
    | Data data -> dataView dispatch data


let view (model: Model) dispatch = Loadable.view (pageView dispatch) model